import { action } from '@reatom/framework'

import { Result } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { refreshWalletsAction } from '@root/entities/wallet/atoms'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'
import { WalletErrorCode } from '@root/shared/api/api-error-code'

import type { WalletId } from '@root/entities/wallet'

/**
 * Making, importing and exporting a wallet whose key we keep.
 *
 * The terminal's older wallets keep their key on the machine, through a secure
 * store only the desktop build has — so in a browser both buttons did nothing
 * at all, with no request and no error, and a web-only account could never
 * hold a wallet. These three go to the server instead, where the signer makes
 * the key and is the only process that ever sees it.
 *
 * Nothing here logs a key, and the import action does not put one in an error
 * either: what comes back on a bad paste is a code, and the words for it are
 * chosen below.
 */

export type WalletCommandError =
  | 'invalid-key'
  | 'already-added'
  | 'held-on-a-device'
  | 'keys-unavailable'
  | 'unknown'

const problemOf = (error: { _tag: string }): WalletCommandError => {
  if (ApiError.isOfCode(WalletErrorCode.InvalidPrivateKey)(error)) {
    return 'invalid-key'
  }
  if (ApiError.isOfCode(WalletErrorCode.WalletAlreadyAdded)(error)) {
    return 'already-added'
  }
  if (ApiError.isOfCode(WalletErrorCode.DeviceWallet)(error)) {
    return 'held-on-a-device'
  }
  if (ApiError.isOfCode(WalletErrorCode.KeysUnavailable)(error)) {
    return 'keys-unavailable'
  }
  return 'unknown'
}

export const walletCommandText: Record<WalletCommandError, string> = {
  'invalid-key':
    'That is not a key we can read. Check you copied the private key and not the address.',
  'already-added': 'That wallet is already on this account.',
  'held-on-a-device':
    'This wallet was added from the desktop app, so its key is on that machine and only it can hand the key back.',
  'keys-unavailable':
    'Wallets cannot be made here at the moment. Nothing was created; try again shortly.',
  unknown: 'That did not go through. Nothing was created.',
}

export const createServerWalletAction = action(
  async (ctx, input: { name?: string; color?: string }) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.wallet.walletCreateWalletCreate, [
        { ...input, network: Network.toDto(Network.Network.Solana) },
      ]),
    )

    if (Result.isSuccess(result)) {
      // The list on screen is built from the wallets request, which does not
      // know about this one yet.
      refreshWalletsAction(ctx)
    }

    return result.pipe(Result.mapError(problemOf))
  },
  'createServerWalletAction',
)

export const importServerWalletAction = action(
  async (ctx, input: { privateKey: string; name?: string; color?: string }) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.wallet.walletImportWalletCreate, [
        { ...input, network: Network.toDto(Network.Network.Solana) },
      ]),
    )

    if (Result.isSuccess(result)) {
      refreshWalletsAction(ctx)
    }

    return result.pipe(Result.mapError(problemOf))
  },
  'importServerWalletAction',
)

/**
 * Several at once, reported one by one.
 *
 * A person moving to us has six wallets and pastes six lines. One bad line
 * must not lose the other five, so each is sent on its own and the failures
 * come back named rather than as a single "that did not work".
 */
export const importServerWalletsAction = action(
  async (ctx, keys: ReadonlyArray<string>) => {
    const done: Array<string> = []
    const failed: Array<WalletCommandError> = []

    for (const privateKey of keys) {
      const result = await ctx.schedule(() =>
        importServerWalletAction(ctx, { privateKey }),
      )
      if (Result.isSuccess(result)) {
        done.push(result.success.address)
      } else {
        failed.push(result.failure)
      }
    }

    return { done, failed }
  },
  'importServerWalletsAction',
)

export const exportServerWalletAction = action(
  async (ctx, walletId: WalletId) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.wallet.walletExportWalletCreate, [
        { walletId },
      ]),
    )

    return result.pipe(Result.mapError(problemOf))
  },
  'exportServerWalletAction',
)
